import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

/**
 * Crops a rendered <svg>-based map so the visible canvas matches the actual
 * geography bounding box plus a small padding.
 *
 * Why this exists: maps use react-simple-maps with a hand-picked
 * `width`/`height`/`center`/`scale`. Each projection leaves different
 * whitespace above/below the geography (the boot of Italy occupies the
 * bottom of an 800x600 canvas with 85px empty above; Canada fills the
 * canvas edge-to-edge with zero margin). That made the stats pill look
 * "detached" from some maps and "stuck to" others — purely an artefact of
 * inconsistent SVG margins, not anything MapStats should know about.
 *
 * We measure the rendered geography paths' bounding box, shrink the wrapper
 * to that height plus padding, and shift the SVG up with negative margin.
 * The wrapper's `overflow: hidden` clips the empty SVG region. Every map's
 * pill-to-geography gap then equals the MapStats spacing plus the same
 * `paddingPx` regardless of projection — no per-map knobs anywhere.
 *
 * The capture region's `toPng` honours `overflow: hidden`, so downloads and
 * product previews see the same cropped output.
 */
const MapAutoFit = ({ children, paddingPx = 8 }) => {
    const innerRef = useRef(null);
    const [crop, setCrop] = useState(null);

    // useLayoutEffect so the first paint already shows the cropped layout
    // instead of flashing the full SVG and then snapping. ResizeObserver and
    // MutationObserver handle subsequent renders (geographies loading,
    // viewport resize).
    useLayoutEffect(() => {
        const inner = innerRef.current;
        if (!inner) return undefined;

        const update = () => {
            const svg = inner.querySelector('svg');
            if (!svg) return;
            const paths = svg.querySelectorAll('path');
            // Skip the empty-skeleton render before geographies load. A few
            // stray paths can exist (e.g. zoom controls); 3 is a safe floor
            // for any real geography rendering.
            if (paths.length < 3) return;

            const innerRect = inner.getBoundingClientRect();
            const svgRect = svg.getBoundingClientRect();
            if (innerRect.width < 10) return;

            // Intersect each path's bbox with the SVG's visible rect so paths
            // that fall outside the viewBox (e.g. Spain's Canary Islands at
            // y=792 in a 600-tall canvas) don't inflate the measured geometry
            // height. They still report a real getBoundingClientRect, but
            // they're clipped from view and shouldn't reserve canvas space.
            let minY = Infinity;
            let maxY = -Infinity;
            paths.forEach((p) => {
                const bb = p.getBoundingClientRect();
                if (bb.height < 1 || bb.width < 1) return;
                const top = Math.max(bb.top, svgRect.top);
                const bottom = Math.min(bb.bottom, svgRect.bottom);
                if (bottom <= top) return;
                minY = Math.min(minY, top);
                maxY = Math.max(maxY, bottom);
            });
            if (!Number.isFinite(minY)) return;

            const geoTop = minY - innerRect.top;
            const geoBottom = maxY - innerRect.top;
            const visibleHeight = Math.round(geoBottom - geoTop + 2 * paddingPx);
            const shiftUp = Math.round(-geoTop + paddingPx);

            setCrop((prev) => {
                if (prev && prev.height === visibleHeight && prev.shift === shiftUp) return prev;
                return { height: visibleHeight, shift: shiftUp };
            });
        };

        update();
        const mo = new MutationObserver(update);
        mo.observe(inner, { childList: true, subtree: true });
        const ro = new ResizeObserver(update);
        ro.observe(inner);
        return () => {
            mo.disconnect();
            ro.disconnect();
        };
    }, [paddingPx]);

    return (
        <Box
            sx={{
                width: '100%',
                height: crop ? `${crop.height}px` : 'auto',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            <Box
                ref={innerRef}
                sx={{
                    marginTop: crop ? `${crop.shift}px` : 0,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

MapAutoFit.propTypes = {
    children: PropTypes.node.isRequired,
    paddingPx: PropTypes.number,
};

export default MapAutoFit;
